import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
	FlaggedOverspeed,
	useOverspeedFlagged,
	useReopenOverspeed,
	useResolveOverspeed,
} from '../api/overspeed'
import { Button } from '../components/button'
import { ClientFormatter } from '../components/client-formatter'
import { Dialog } from '../components/dialog'
import { Input } from '../components/input'
import { VehicleFormatter } from '../components/vehicle-formatter'

type Tab = 'open' | 'resolved' | 'all'

const RESOLUTION_LABEL: Record<string, string> = {
	fine: 'Pokutováno',
	// 'warning' se už nově nezakládá (tlačítko Upozornit bylo zrušeno), label
	// zůstává jen kvůli zobrazení starších případů.
	warning: 'Upozorněno',
	dismissed: 'Zamítnuto',
}

function formatDateShort(iso: string): string {
	const date = new Date(iso)
	if (Number.isNaN(date.getTime())) return iso
	return date.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Popisek stavu vyřízení — u zamítnutí rozlišuje trvale / na týden (do kdy). */
function resolutionLabel(row: FlaggedOverspeed): string {
	if (row.resolution === 'dismissed') {
		return row.snoozed_until
			? `Zamítnuto do ${formatDateShort(row.snoozed_until)}`
			: 'Zamítnuto natrvalo'
	}
	return RESOLUTION_LABEL[row.resolution ?? ''] ?? 'Vyřízeno'
}

function FineDialog({
	row,
	onClose,
	onSubmit,
}: {
	row: FlaggedOverspeed
	onClose: () => void
	onSubmit: (amount: number | undefined, note: string) => void
}) {
	const [amount, setAmount] = useState('')
	const [note, setNote] = useState('')

	return (
		<Dialog heading="Pokutovat za překročení rychlosti" isOpen onOpenChange={onClose}>
			<div className="m-4 min-w-[24rem] space-y-4">
				<p className="text-sm text-gray-600">
					{row.incident_count}× překročení, max {row.max_speed} km/h. Zadej částku pokuty
					— založí se jako poplatek na rezervaci.
				</p>
				<Input
					label="Částka pokuty (Kč)"
					type="number"
					value={amount}
					onChange={setAmount}
				/>
				<Input label="Poznámka (volitelné)" value={note} onChange={setNote} />
				<div className="flex justify-end gap-2">
					<Button variant="SECONDARY" size="SMALL" onPress={onClose}>
						Zrušit
					</Button>
					<Button
						variant="PRIMARY"
						size="SMALL"
						onPress={() => onSubmit(amount ? Number(amount) : undefined, note)}
					>
						Pokutovat
					</Button>
				</div>
			</div>
		</Dialog>
	)
}

const COLLAPSE_KEY = 'overspeed-panel-collapsed'

export function OverspeedPanel() {
	const [tab, setTab] = useState<Tab>('open')
	const [fineFor, setFineFor] = useState<FlaggedOverspeed | null>(null)
	// Panel je defaultně zavřený (úzký pruh); rozbalí se až na klik.
	const [collapsed, setCollapsed] = useState<boolean>(() => {
		try {
			return localStorage.getItem(COLLAPSE_KEY) !== '0'
		} catch {
			return true
		}
	})
	const { data = [], isLoading } = useOverspeedFlagged({
		status: tab === 'all' ? undefined : tab,
	})
	const { mutate: resolve } = useResolveOverspeed()
	const { mutate: reopen } = useReopenOverspeed()

	const toggleCollapsed = (value: boolean) => {
		setCollapsed(value)
		try {
			localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0')
		} catch {
			// localStorage nedostupné – jen nepřetrvá
		}
	}

	// Skrytý panel – zůstane jen úzký pruh s možností zobrazit.
	if (collapsed) {
		const openCount = tab === 'open' ? data.length : 0
		return (
			<div className="mb-6 flex items-center justify-between rounded-xl border border-rose-200 bg-rose-50/50 px-4 py-2">
				<span className="text-sm font-semibold text-rose-800">
					Překročení rychlosti
					{openCount ? (
						<span className="ml-2 rounded bg-rose-600 px-2 py-0.5 text-xs font-medium text-white">
							{openCount} nevyřízených
						</span>
					) : null}
				</span>
				<Button variant="SECONDARY" size="SMALL" onPress={() => toggleCollapsed(false)}>
					Zobrazit
				</Button>
			</div>
		)
	}

	if (isLoading && data.length === 0) return null
	if (tab === 'open' && data.length === 0) return null

	const fineable = data.filter(r => r.case_status === 'open' && r.is_short_term).length

	const tabClass = (active: boolean) =>
		`rounded-md px-3 py-1.5 text-sm font-medium ${
			active ? 'bg-rose-600 text-white' : 'bg-white text-gray-700 hover:bg-rose-100'
		}`

	const submitFine = (amount: number | undefined, note: string) => {
		if (!fineFor) return
		resolve({ reservationId: fineFor.reservation_id, resolution: 'fine', amount, note })
		setFineFor(null)
	}

	return (
		<div className="mb-6 rounded-xl border border-rose-200 bg-rose-50/50">
			<div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
				<span className="font-semibold text-rose-800">
					Překročení rychlosti
					{fineable ? ` · ${fineable} k pokutě` : ''}
				</span>
				<div className="flex gap-2">
					<button
						type="button"
						className={tabClass(tab === 'open')}
						onClick={() => setTab('open')}
					>
						Nevyřízené
					</button>
					<button
						type="button"
						className={tabClass(tab === 'resolved')}
						onClick={() => setTab('resolved')}
					>
						Vyřízené
					</button>
					<button
						type="button"
						className={tabClass(tab === 'all')}
						onClick={() => setTab('all')}
					>
						Vše
					</button>
					<Button variant="SECONDARY" size="SMALL" onPress={() => toggleCollapsed(true)}>
						Skrýt
					</Button>
				</div>
			</div>

			<div className="overflow-x-auto px-2 pb-3">
				{data.length === 0 ? (
					<p className="px-2 py-2 text-sm text-gray-500">Žádné záznamy.</p>
				) : (
					<table className="w-full text-left text-sm">
						<thead className="text-gray-500">
							<tr>
								<th className="px-2 py-1 font-medium">Vozidlo</th>
								<th className="px-2 py-1 font-medium">Zákazník</th>
								<th className="px-2 py-1 font-medium">Typ</th>
								<th className="px-2 py-1 font-medium">Překročení</th>
								<th className="px-2 py-1 font-medium">Max.</th>
								<th className="px-2 py-1 font-medium">Stav</th>
								<th className="px-2 py-1 font-medium"></th>
							</tr>
						</thead>
						<tbody>
							{data.map(row => (
								<tr
									key={row.reservation_id}
									className="border-t border-rose-100 align-top"
								>
									<td className="px-2 py-1.5">
										<VehicleFormatter id={row.vehicle_id} />
									</td>
									<td className="px-2 py-1.5">
										<ClientFormatter id={row.client_id} />
									</td>
									<td className="px-2 py-1.5">
										{row.is_short_term ? (
											<span className="rounded bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-800">
												Krátkodobý
											</span>
										) : (
											<span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
												Dlouhodobý
											</span>
										)}
									</td>
									<td className="px-2 py-1.5">{row.incident_count}×</td>
									<td className="px-2 py-1.5 font-semibold text-rose-700">
										{row.max_speed} km/h
									</td>
									<td className="px-2 py-1.5">
										{row.case_status === 'resolved' ? (
											<span className="text-xs text-gray-600">
												{resolutionLabel(row)}
												{row.fine_amount != null
													? ` · ${row.fine_amount} Kč`
													: ''}
											</span>
										) : (
											<span className="rounded bg-rose-600 px-2 py-0.5 text-xs font-medium text-white">
												Nevyřízeno
											</span>
										)}
									</td>
									<td className="px-2 py-1.5">
										<div className="flex flex-wrap justify-end gap-1.5">
											<Link
												className="rounded px-2 py-1 text-xs text-blue-600 hover:underline"
												to={`/svc/reservation/${row.reservation_id}`}
											>
												Detail
											</Link>
											{row.case_status === 'open' ? (
												<>
													<Button
														variant="PRIMARY"
														size="SMALL"
														onPress={() => setFineFor(row)}
													>
														Pokutovat
													</Button>
													<Button
														variant="SECONDARY"
														size="SMALL"
														onPress={() =>
															resolve({
																reservationId: row.reservation_id,
																resolution: 'dismissed',
																snoozeDays: 7,
															})
														}
													>
														Zamítnout na týden
													</Button>
													<Button
														variant="SECONDARY"
														size="SMALL"
														onPress={() =>
															resolve({
																reservationId: row.reservation_id,
																resolution: 'dismissed',
															})
														}
													>
														Zamítnout trvale
													</Button>
												</>
											) : (
												<Button
													variant="SECONDARY"
													size="SMALL"
													onPress={() => reopen(row.reservation_id)}
												>
													Znovu otevřít
												</Button>
											)}
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{fineFor ? (
				<FineDialog row={fineFor} onClose={() => setFineFor(null)} onSubmit={submitFine} />
			) : null}
		</div>
	)
}
